import React, { useState, useEffect, useRef } from 'react'
import {
  Timer, CheckCircle, XCircle, ChevronRight,
  RotateCcw, Trophy, AlertTriangle, BookOpen, ChevronDown
} from 'lucide-react'
import { useProgressStore } from '../store/progressStore'

const UNIT_ID = 'S1-1'
const PASS_SCORE = 70
const TIME_LIMIT = 25 * 60 // 25분

interface TestQuestion {
  id: string
  number: number
  category: string
  difficulty: 'easy' | 'medium' | 'hard'
  question: string
  choices: string[]
  answer: number
  explanation: string
  concept: string
  misconceptionCode: string
  learnStepId: string
}

const TEST_QUESTIONS: TestQuestion[] = [
  {
    id: 'Q01', number: 1, category: '변수의 종류',
    difficulty: 'easy',
    question: '다음 중 질적 변수(범주형 변수)에 해당하는 것은?',
    choices: ['키', '몸무게', '혈액형', '하루 수면 시간'],
    answer: 2,
    explanation: '혈액형(A, B, O, AB)은 범주로 구분되는 질적 변수입니다. 키·몸무게·수면시간은 수치로 측정되는 양적 변수입니다.',
    concept: '질적·양적 변수 구분', misconceptionCode: 'SP01', learnStepId: 'SA01',
  },
  {
    id: 'Q02', number: 2, category: '변수의 종류',
    difficulty: 'easy',
    question: '다음 중 양적 변수(연속형)에 해당하는 것은?',
    choices: ['학급의 학생 수', '가족의 형제 수', '오늘 독서한 권 수', '100m 달리기 기록(초)'],
    answer: 3,
    explanation: '달리기 기록(12.34초 등)은 소수점으로 측정 가능한 연속형 양적 변수입니다. 나머지는 정수로만 셀 수 있는 이산형 변수입니다.',
    concept: '연속형 양적 변수', misconceptionCode: 'SP01', learnStepId: 'SA01',
  },
  {
    id: 'Q03', number: 3, category: '줄기잎 그림',
    difficulty: 'easy',
    question: '줄기잎 그림에서 "4 | 2 5 9"가 나타내는 자료의 값들은?',
    choices: ['4, 2, 5, 9', '42, 45, 49', '24, 54, 94', '425, 459'],
    answer: 1,
    explanation: '줄기잎 그림에서 줄기(|왼쪽)는 십의 자리, 잎(|오른쪽)은 일의 자리를 나타냅니다. "4|2 5 9"는 42, 45, 49를 의미합니다.',
    concept: '줄기잎 그림 읽기', misconceptionCode: 'SP02', learnStepId: 'SA02',
  },
  {
    id: 'Q04', number: 4, category: '줄기잎 그림',
    difficulty: 'easy',
    question: '다음 줄기잎 그림에서 중앙값은?\n1 | 3 7\n2 | 0 5\n3 | 2',
    choices: ['17', '20', '21', '25'],
    answer: 1,
    explanation: '자료: 13, 17, 20, 25, 32 → 5개, 가운데(3번째) 값 = 20\n중앙값 = 20',
    concept: '줄기잎 그림 중앙값', misconceptionCode: 'SP02', learnStepId: 'SA02',
  },
  {
    id: 'Q05', number: 5, category: '도수분포표',
    difficulty: 'easy',
    question: '계급이 "30 이상 40 미만"일 때 계급값은?',
    choices: ['30', '35', '40', '10'],
    answer: 1,
    explanation: '계급값 = (하한값 + 상한값) ÷ 2 = (30 + 40) ÷ 2 = 35',
    concept: '계급값 계산', misconceptionCode: 'SP03', learnStepId: 'SA03',
  },
  {
    id: 'Q06', number: 6, category: '도수분포표',
    difficulty: 'easy',
    question: '계급의 크기가 5이고 하한값이 20인 계급의 상한값은?',
    choices: ['20', '22.5', '25', '30'],
    answer: 2,
    explanation: '계급의 크기 = 상한 - 하한이므로, 상한 = 하한 + 계급의 크기 = 20 + 5 = 25',
    concept: '계급의 크기', misconceptionCode: 'SP03', learnStepId: 'SA03',
  },
  {
    id: 'Q07', number: 7, category: '상대도수',
    difficulty: 'easy',
    question: '전체 도수가 50이고 어떤 계급의 도수가 15일 때, 그 계급의 상대도수는?',
    choices: ['0.15', '0.30', '0.50', '3.33'],
    answer: 1,
    explanation: '상대도수 = 도수 ÷ 전체도수 = 15 ÷ 50 = 0.3',
    concept: '상대도수 계산', misconceptionCode: 'SP04', learnStepId: 'SA04',
  },
  {
    id: 'Q08', number: 8, category: '상대도수',
    difficulty: 'medium',
    question: '어떤 계급의 상대도수가 0.25이고 전체 도수가 80일 때, 그 계급의 도수는?',
    choices: ['8명', '20명', '25명', '32명'],
    answer: 1,
    explanation: '도수 = 상대도수 × 전체도수 = 0.25 × 80 = 20명',
    concept: '상대도수 역산', misconceptionCode: 'SP04', learnStepId: 'SA04',
  },
  {
    id: 'Q09', number: 9, category: '상대도수',
    difficulty: 'easy',
    question: '모든 계급의 상대도수를 더하면?',
    choices: ['0', '0.5', '1', '계급 수'],
    answer: 2,
    explanation: '모든 계급의 상대도수의 합은 항상 1입니다. (전체 도수 ÷ 전체 도수 = 1)',
    concept: '상대도수의 성질', misconceptionCode: 'SP04', learnStepId: 'SA04',
  },
  {
    id: 'Q10', number: 10, category: '누적도수',
    difficulty: 'medium',
    question: '도수분포표에서 각 계급의 도수가 4, 6, 10, 8, 2일 때, 세 번째 계급까지의 누적도수는?',
    choices: ['10', '16', '20', '30'],
    answer: 2,
    explanation: '누적도수 = 처음부터 해당 계급까지 도수의 합\n= 4 + 6 + 10 = 20',
    concept: '누적도수 계산', misconceptionCode: 'SP05', learnStepId: 'SA05',
  },
  {
    id: 'Q11', number: 11, category: '누적도수',
    difficulty: 'medium',
    question: '전체 도수가 40이고 세 번째 계급까지 누적도수가 28일 때, 누적상대도수는?',
    choices: ['0.28', '0.5', '0.7', '0.75'],
    answer: 2,
    explanation: '누적상대도수 = 누적도수 ÷ 전체도수 = 28 ÷ 40 = 0.7',
    concept: '누적상대도수', misconceptionCode: 'SP05', learnStepId: 'SA05',
  },
  {
    id: 'Q12', number: 12, category: '히스토그램',
    difficulty: 'easy',
    question: '히스토그램과 일반 막대 그래프의 가장 큰 차이점은?',
    choices: ['색상이 다르다', '히스토그램은 막대 사이에 간격이 없다', '히스토그램은 세로 막대를 사용한다', '크기가 다르다'],
    answer: 1,
    explanation: '히스토그램은 연속형 자료의 분포를 나타내므로 막대 사이에 간격이 없습니다. 일반 막대그래프는 범주형 자료로 막대 사이에 간격이 있습니다.',
    concept: '히스토그램의 특징', misconceptionCode: 'SP03', learnStepId: 'SA04',
  },
  {
    id: 'Q13', number: 13, category: '도수분포다각형',
    difficulty: 'medium',
    question: '도수분포다각형에서 각 점의 x좌표로 사용하는 값은?',
    choices: ['계급의 하한값', '계급의 상한값', '계급값 (계급의 중간값)', '도수'],
    answer: 2,
    explanation: '도수분포다각형에서 x좌표는 계급값((하한+상한)÷2)을 사용하고, y좌표는 해당 계급의 도수를 사용합니다.',
    concept: '도수분포다각형 작성', misconceptionCode: 'SP03', learnStepId: 'SA04',
  },
  {
    id: 'Q14', number: 14, category: '상대도수 비교',
    difficulty: 'hard',
    question: 'A반(30명) 수학 점수 80점 이상 도수=9명, B반(50명) 80점 이상 도수=15명. 80점 이상 비율이 높은 반은?',
    choices: ['A반', 'B반', '같다', '비교 불가'],
    answer: 0,
    explanation: 'A반 상대도수 = 9÷30 = 0.3 (30%)\nB반 상대도수 = 15÷50 = 0.3 (30%)\n두 반의 80점 이상 비율은 같습니다!\n집단 크기가 다를 때는 반드시 상대도수로 비교해야 합니다.',
    concept: '상대도수로 비교', misconceptionCode: 'SP04', learnStepId: 'SA04',
  },
  {
    id: 'Q15', number: 15, category: '종합',
    difficulty: 'hard',
    question: '전체 학생 60명의 수학 점수 자료에서 60점 이상 70점 미만: 12명, 70점 이상 80점 미만: 18명, 80점 이상: 30명일 때, 80점 미만 학생의 누적상대도수는?',
    choices: ['0.3', '0.5', '0.6', '0.8'],
    answer: 1,
    explanation: '80점 미만 누적도수 = 12 + 18 = 30명\n누적상대도수 = 30 ÷ 60 = 0.5\n전체 학생의 50%가 80점 미만입니다.',
    concept: '누적상대도수 종합', misconceptionCode: 'SP05', learnStepId: 'SA05',
  },
]

type Phase = 'ready' | 'testing' | 'done'

export default function S1_1TestPage({ onGoLearn }: { onGoLearn?: (stepId: string) => void }) {
  const [phase, setPhase] = useState<Phase>('ready')
  const [answers, setAnswers] = useState<(number | null)[]>(Array(TEST_QUESTIONS.length).fill(null))
  const [timeLeft, setTimeLeft] = useState(TIME_LIMIT)
  const [current, setCurrent] = useState(0)
  const [showExp, setShowExp] = useState<Record<number, boolean>>({})
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { recordTestResult, recordMisconceptions } = useProgressStore(s => ({
    recordTestResult: s.recordTestResult,
    recordMisconceptions: s.recordMisconceptions,
  }))

  useEffect(() => {
    if (phase === 'testing') {
      timerRef.current = setInterval(() => {
        setTimeLeft(t => {
          if (t <= 1) { clearInterval(timerRef.current!); finishTest(); return 0 }
          return t - 1
        })
      }, 1000)
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [phase])

  const finishTest = () => {
    if (timerRef.current) clearInterval(timerRef.current)
    const correct = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
    const sc = Math.round((correct / TEST_QUESTIONS.length) * 100)
    const seconds = TIME_LIMIT - timeLeft
    const wrongCodes = TEST_QUESTIONS
      .filter((q, i) => answers[i] !== q.answer)
      .map(q => q.misconceptionCode)
      .filter((v, i, arr) => arr.indexOf(v) === i)
    if (wrongCodes.length > 0) recordMisconceptions(UNIT_ID, wrongCodes)
    recordTestResult(UNIT_ID, {
      unitId: UNIT_ID,
      score: sc,
      correctCount: correct,
      totalCount: TEST_QUESTIONS.length,
      durationSec: seconds,
      answers: answers.map(a => a ?? -1),
      wrongConceptIds: TEST_QUESTIONS
        .filter((q, i) => answers[i] !== q.answer)
        .map(q => q.concept),
      grade: sc >= 90 ? 'A' : sc >= 80 ? 'B' : sc >= 70 ? 'C' : sc >= 60 ? 'D' : 'F',
      passed: sc >= PASS_SCORE,
    })
    setPhase('done')
  }

  const fmt = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
  const correct = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
  const score = Math.round((correct / TEST_QUESTIONS.length) * 100)
  const passed = score >= PASS_SCORE

  const diffColor = { easy: 'bg-emerald-100 text-emerald-700', medium: 'bg-amber-100 text-amber-700', hard: 'bg-red-100 text-red-700' }
  const diffLabel = { easy: '쉬움', medium: '보통', hard: '어려움' }

  // ── 준비 화면 ──
  if (phase === 'ready') {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="rounded-2xl bg-gradient-to-br from-violet-600 to-purple-700 text-white p-6 mb-6 text-center">
          <p className="text-4xl mb-3">📊</p>
          <h2 className="text-2xl font-bold mb-2">자료의 정리와 해석 실전 TEST</h2>
          <p className="text-violet-100 text-sm">S1-1 단원 전체 평가</p>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          {[
            { icon: '📝', label: '총 문항', value: `${TEST_QUESTIONS.length}문항` },
            { icon: '⏱️', label: '제한 시간', value: '25분' },
            { icon: '🎯', label: '합격 기준', value: `${PASS_SCORE}점 이상` },
            { icon: '📊', label: '난이도', value: '쉬움~어려움' },
          ].map(item => (
            <div key={item.label} className="card border border-slate-200 text-center">
              <p className="text-2xl mb-1">{item.icon}</p>
              <p className="text-xs text-slate-400">{item.label}</p>
              <p className="text-sm font-bold text-slate-800">{item.value}</p>
            </div>
          ))}
        </div>

        <div className="card border border-slate-200 mb-6">
          <h3 className="font-bold text-slate-800 mb-3">📋 출제 범위</h3>
          <div className="space-y-2">
            {[
              { step: 'SA01', title: '변수와 자료의 종류', count: 2, color: 'bg-sky-100 text-sky-700' },
              { step: 'SA02', title: '줄기잎 그림', count: 2, color: 'bg-emerald-100 text-emerald-700' },
              { step: 'SA03', title: '도수분포표·계급값', count: 2, color: 'bg-orange-100 text-orange-700' },
              { step: 'SA04', title: '히스토그램·상대도수', count: 6, color: 'bg-violet-100 text-violet-700' },
              { step: 'SA05', title: '누적도수·누적상대도수', count: 3, color: 'bg-rose-100 text-rose-700' },
            ].map(item => (
              <div key={item.step} className="flex items-center gap-3">
                <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${item.color}`}>{item.step}</span>
                <span className="text-sm text-slate-700 flex-1">{item.title}</span>
                <span className="text-xs text-slate-400">{item.count}문제</span>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={() => { setPhase('testing'); setTimeLeft(TIME_LIMIT) }}
          className="w-full py-4 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white rounded-2xl font-bold text-lg transition-all shadow-lg"
        >
          시험 시작하기 🚀
        </button>
      </div>
    )
  }

  // ── 시험 화면 ──
  if (phase === 'testing') {
    const q = TEST_QUESTIONS[current]
    const answered = answers.filter(a => a !== null).length
    return (
      <div className="p-4 max-w-2xl mx-auto">
        {/* 상단 상태바 */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-slate-700">{current + 1}/{TEST_QUESTIONS.length}</span>
            <div className="h-1.5 w-32 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-violet-500 rounded-full transition-all"
                style={{ width: `${((current + 1) / TEST_QUESTIONS.length) * 100}%` }} />
            </div>
          </div>
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold ${
            timeLeft < 300 ? 'bg-red-100 text-red-600' : 'bg-violet-100 text-violet-700'
          }`}>
            <Timer size={14} />
            {fmt(timeLeft)}
          </div>
        </div>

        {/* 문제 카드 */}
        <div className="card border-2 border-violet-100 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-bold bg-violet-600 text-white px-2 py-0.5 rounded-full">Q{q.number}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${diffColor[q.difficulty]}`}>
              {diffLabel[q.difficulty]}
            </span>
            <span className="text-xs text-slate-400">{q.category}</span>
          </div>
          <p className="text-base font-semibold text-slate-800 leading-relaxed mb-4 whitespace-pre-line">{q.question}</p>
          <div className="space-y-2">
            {q.choices.map((choice, i) => (
              <button
                key={i}
                onClick={() => {
                  const next = [...answers]; next[current] = i; setAnswers(next)
                }}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 font-medium text-sm transition-all ${
                  answers[current] === i
                    ? 'border-violet-500 bg-violet-50 text-violet-800'
                    : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'
                }`}
              >
                <span className="font-bold mr-2">{['①', '②', '③', '④'][i]}</span>{choice}
              </button>
            ))}
          </div>
        </div>

        {/* 이동 버튼 */}
        <div className="flex gap-2 mb-4">
          <button onClick={() => setCurrent(c => Math.max(0, c - 1))}
            disabled={current === 0}
            className="px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 disabled:opacity-40 hover:bg-slate-50">
            ← 이전
          </button>
          {current < TEST_QUESTIONS.length - 1 ? (
            <button onClick={() => setCurrent(c => c + 1)}
              className="flex-1 py-2.5 rounded-xl bg-violet-600 text-white font-bold text-sm hover:bg-violet-700 flex items-center justify-center gap-1">
              다음 <ChevronRight size={14} />
            </button>
          ) : (
            <button
              onClick={finishTest}
              disabled={answered < TEST_QUESTIONS.length}
              className="flex-1 py-2.5 rounded-xl bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400"
            >
              제출하기 ({answered}/{TEST_QUESTIONS.length})
            </button>
          )}
        </div>

        {/* 문항 번호 빠른 이동 */}
        <div className="flex flex-wrap gap-1.5">
          {TEST_QUESTIONS.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)}
              className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${
                i === current ? 'bg-violet-600 text-white' :
                answers[i] !== null ? 'bg-violet-100 text-violet-700' :
                'bg-slate-100 text-slate-400'
              }`}>
              {i + 1}
            </button>
          ))}
        </div>
      </div>
    )
  }

  // ── 결과 화면 ──
  return (
    <div className="p-4 max-w-2xl mx-auto">
      {/* 결과 헤더 */}
      <div className={`rounded-2xl p-6 mb-5 text-center text-white bg-gradient-to-br ${
        passed ? 'from-emerald-500 to-teal-600' : 'from-slate-600 to-slate-700'
      }`}>
        <p className="text-5xl mb-2">{passed ? '🏆' : '📚'}</p>
        <p className="text-3xl font-black mb-1">{score}점</p>
        <p className="text-lg font-bold opacity-90">{passed ? '합격! 통계 마스터!' : '아직 조금 더 연습이 필요해요!'}</p>
        <p className="text-sm opacity-75 mt-1">{correct}/{TEST_QUESTIONS.length} 정답 · {passed ? '✅ 합격' : `❌ 불합격 (기준 ${PASS_SCORE}점)`}</p>
      </div>

      {/* 성취 뱃지 */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          { icon: '📋', label: '자료 종류', steps: ['SA01', 'SA02'] },
          { icon: '📊', label: '도수분포', steps: ['SA03', 'SA04'] },
          { icon: '📈', label: '누적도수', steps: ['SA05'] },
        ].map(group => {
          const groupQs = TEST_QUESTIONS.filter(q => group.steps.includes(q.learnStepId))
          const groupCorrect = groupQs.filter(q => {
            const idx = TEST_QUESTIONS.findIndex(tq => tq.id === q.id)
            return answers[idx] === q.answer
          }).length
          const pct = Math.round((groupCorrect / groupQs.length) * 100)
          return (
            <div key={group.label} className="card border border-slate-200 text-center">
              <p className="text-xl mb-1">{group.icon}</p>
              <p className="text-xs font-bold text-slate-700">{group.label}</p>
              <p className={`text-sm font-black mt-0.5 ${pct >= 70 ? 'text-emerald-600' : 'text-red-500'}`}>{pct}%</p>
            </div>
          )
        })}
      </div>

      {/* 문제별 결과 */}
      <div className="card border border-slate-200 mb-5">
        <h3 className="font-bold text-slate-800 mb-3">📋 문항별 결과</h3>
        <div className="space-y-2">
          {TEST_QUESTIONS.map((q, i) => {
            const isCorrect = answers[i] === q.answer
            return (
              <div key={q.id}>
                <button
                  onClick={() => setShowExp(s => ({ ...s, [i]: !s[i] }))}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all ${
                    isCorrect ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
                  }`}
                >
                  {isCorrect
                    ? <CheckCircle size={16} className="text-emerald-500 flex-shrink-0" />
                    : <XCircle size={16} className="text-red-500 flex-shrink-0" />}
                  <span className="text-sm font-semibold text-slate-700 flex-1 text-left">Q{q.number}. {q.category}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${diffColor[q.difficulty]}`}>
                    {diffLabel[q.difficulty]}
                  </span>
                  <ChevronDown size={13} className={`text-slate-400 transition-transform ${showExp[i] ? 'rotate-180' : ''}`} />
                </button>
                {showExp[i] && (
                  <div className="mt-1 p-3 bg-white border border-slate-200 rounded-xl text-xs space-y-1.5 ml-2">
                    <p className="text-slate-700 font-medium whitespace-pre-line">{q.question}</p>
                    {!isCorrect && (
                      <p className="text-red-600">
                        내 답: {answers[i] !== null ? q.choices[answers[i]] : '미응답'}
                      </p>
                    )}
                    <p className="text-emerald-700 font-semibold">
                      정답: {q.choices[q.answer]}
                    </p>
                    <p className="text-slate-500 whitespace-pre-line">{q.explanation}</p>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* 오개념 안내 */}
      {!passed && (
        <div className="card border border-amber-200 bg-amber-50 mb-5">
          <h3 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
            <AlertTriangle size={16} /> 취약 개념 안내
          </h3>
          {TEST_QUESTIONS
            .filter((q, i) => answers[i] !== q.answer)
            .map(q => q.misconceptionCode)
            .filter((v, i, arr) => arr.indexOf(v) === i)
            .map(code => (
              <div key={code} className="flex items-start gap-2 mb-2">
                <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-mono font-bold flex-shrink-0 mt-0.5">{code}</span>
                <p className="text-xs text-amber-700">
                  {code === 'SP01' && '변수 종류 혼동 (질적·이산형·연속형) → SA01 개념 재학습 권장'}
                  {code === 'SP02' && '줄기잎 그림 읽기 오류 → SA02 개념 재학습 권장'}
                  {code === 'SP03' && '계급값·계급의 크기 혼동 → SA03 개념 재학습 권장'}
                  {code === 'SP04' && '상대도수 계산·비교 오류 → SA04 개념 재학습 권장'}
                  {code === 'SP05' && '누적도수·누적상대도수 오류 → SA05 개념 재학습 권장'}
                </p>
              </div>
            ))}
          <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
            <BookOpen size={11} /> 개념 학습 탭에서 해당 단계를 다시 학습해보세요!
          </p>
          {onGoLearn && (
            <div className="mt-2 space-y-1">
              {TEST_QUESTIONS
                .filter((q, i) => answers[i] !== q.answer)
                .map((q, _, arr) => arr.find(x => x.learnStepId === q.learnStepId))
                .filter((q, i, arr) => q && arr.findIndex(x => x?.learnStepId === q.learnStepId) === i)
                .map(q => q && (
                  <button key={q.learnStepId}
                    onClick={() => onGoLearn(q.learnStepId)}
                    className="w-full text-left text-xs px-3 py-1.5 rounded-lg bg-amber-100 text-amber-800 hover:bg-amber-200 font-medium flex items-center gap-1.5">
                    <BookOpen size={11} /> {q.learnStepId} 개념 다시 학습하기 →
                  </button>
                ))}
            </div>
          )}
        </div>
      )}

      {/* 하단 버튼 */}
      <div className="flex gap-3">
        <button
          onClick={() => {
            setPhase('ready')
            setAnswers(Array(TEST_QUESTIONS.length).fill(null))
            setTimeLeft(TIME_LIMIT)
            setCurrent(0)
            setShowExp({})
          }}
          className="flex-1 py-3 rounded-2xl border-2 border-slate-200 text-slate-700 font-bold text-sm hover:bg-slate-50 flex items-center justify-center gap-2"
        >
          <RotateCcw size={15} /> 다시 풀기
        </button>
        {passed && (
          <div className="flex-1 py-3 rounded-2xl bg-emerald-500 text-white font-bold text-sm flex items-center justify-center gap-2">
            <Trophy size={15} /> 통계 완료! 🎉
          </div>
        )}
      </div>
    </div>
  )
}
